import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsService } from '@ddt-web/analytics';
import { ERROR_SEVERITY, ErrorDCR } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { PortfolioIdUtil } from '@ddt-web/portfolio-data';

import { ErrorObject } from '../../models/error-object';
import { PopupDialogAction } from '../../models/popup-dialog-object';
import { AbstractErrorHandlerService } from '../abstract-error-handler.service';
import { ErrorHelperService } from '../error-helper.service';
import { ErrorPopupService } from '../error-popup.service';

/**
 * This error handler is to be used when on asset resolve failure, we want to display the list of missing securities in error dialog
 */
@Injectable()
export class SecurityDetailsProcessingErrorHandler extends AbstractErrorHandlerService {
	//When this flag is TRUE, we display the button 'Review Holdings' and redirect to PE, when FALSE, we just display the list of missing securities and display 'Continue'
	@InjectConfig(SharedConfigKey.ENABLE_HARD_STOP_WITH_PE) private readonly enableHardStopWithReview = true;
	private readonly STORES_TO_HANDLE = ['taxDetails', 'positionDetails']; // positionDetails is required for API timeout/ error handling

	constructor(
		protected contentService: ContentService,
		protected errorPopupService: ErrorPopupService,
		protected tealiumAnalyticsService: TealiumAnalyticsService,
		protected errorHelperService: ErrorHelperService
	) {
		super(contentService, errorPopupService, tealiumAnalyticsService, errorHelperService);
	}

	public handleError(error): void {
		// TODO agrigore: clean this up and reuse in IQ
		if (this.STORES_TO_HANDLE.includes(error?.configName)) {
			const errorTitle = this.contentService.get('SOMETHING_WRONG_ERROR_TITLE', ErrorDCR.SOMETHING_WRONG_ERROR_TITLE);
			const securities = Array.from(error.responseJson?.assets?.keys() ?? []);
			if (securities.length || error?.responseJson?.isPartialError) {
				this.handlePartialSecuritiesMissing(error, errorTitle);
			} else {
				this.handleAllAnalysisFailing(error, errorTitle,
					this.contentService.get('ALL_SECURITIES_MISSING_ERROR', 'There seems to be some issue in resolving your securities')
				);
			}
		} else {
			this.nextHandler.handleError(error);
		}
	}

	private handleAllAnalysisFailing(error: ErrorObject, errorTitle: string, errorMessage: string): void {
		this.errorSeverity = ERROR_SEVERITY.CRITICAL;
		this.trackErrorMessage = 'unabletoprocessrequest';
		this.tealiumAnalyticsService.trackError(this.trackErrorMessage, this.errorSeverity);

		const actions = [
			PopupDialogAction.create({
				label: this.contentService.get('ERROR_NEW_ANALYSIS_LABEL', ErrorDCR.ERROR_NEW_ANALYSIS_LABEL),
				type: 'view_new_analysis',
				actionClass: 'btn-secondary',
				reload: false
			}),
			PopupDialogAction.create({ label: this.contentService.get('ERROR_RETRY_LABEL', ErrorDCR.ERROR_RETRY_LABEL), reload: true })
		];

		console.error(errorMessage, error);
		const requestId = error?.responseJson?.error?.['error-id'];
		this.errorPopupService.openResponseMessageDialog(
			this.buildErrorMessage(false, 'error-security-dialog', errorTitle, this.trackErrorMessage, errorMessage, true, [], actions, requestId)
		);
	}

	private handlePartialSecuritiesMissing(error: ErrorObject, errorTitle: string): void {
		this.errorSeverity = ERROR_SEVERITY.WARNING;
		this.trackErrorMessage = 'partialsecurityresolution';
		this.tealiumAnalyticsService.trackError(this.trackErrorMessage, this.errorSeverity);

		let errorMessage = this.contentService.get('PARTIAL_MISSING_SECURITIES_ERROR', 'There seems to be some issue in resolving some of your securities');

		const errorCodes = Array.from(error.responseJson.errorCodes.keys() ?? []);
		const securitiesMissing = errorCodes.map(holding => {
			return { iconType: 'times', description: (<any>holding).split('@')?.[0] };
		});
		console.error(errorMessage, error, securitiesMissing);

		let actions = [
			PopupDialogAction.create({
				label: this.contentService.get('ERROR_CONTINUE_LABEL', ErrorDCR.ERROR_CONTINUE_LABEL),
				type: 'continue',
				reload: false
			})
		];

		if (PortfolioIdUtil.isModel(error?.responseJson?.errorPortfolios?.[0]?.id)) {
			errorMessage = this.contentService.get('PARTIAL_MISSING_SECURITIES_ERROR_WITHOUT_REVIEW', 'There seems to be some issue in resolving some of your securities');
			actions = [
				PopupDialogAction.create({
					label: this.contentService.get('ERROR_NEW_ANALYSIS_LABEL', ErrorDCR.ERROR_NEW_ANALYSIS_LABEL),
					type: 'view_new_analysis',
					reload: false
				})
			];
		}
		else {
			if (error?.configName === 'positionDetails' && this.enableHardStopWithReview) {
				actions = [
					PopupDialogAction.create({
						label: this.contentService.get('ERROR_NEW_ANALYSIS_LABEL', ErrorDCR.ERROR_NEW_ANALYSIS_LABEL),
						type: 'view_new_analysis',
						reload: false,
						actionClass: 'btn-secondary'
					})
				];
				actions.push(PopupDialogAction.create({
					label: this.contentService.get('ERROR_REVIEW_PORTFOLIO_HOLDINGS_LABEL', ErrorDCR.ERROR_REVIEW_PORTFOLIO_HOLDINGS_LABEL),
					type: 'view_portfolio_editor',
					reload: false,
					portfolioId: error?.responseJson?.errorPortfolios?.[0]?.id
				}));
			}
		}

		this.errorPopupService.openResponseMessageDialog(
			this.buildErrorMessage(false, 'error-security-dialog', errorTitle, this.trackErrorMessage, errorMessage, true, securitiesMissing, actions),
			true,
			'730px',
			'md',
			false
		);
	}
}
